import type {
  MediaType,
  MediaApi,
  Client,
  User,
  MediaMember,
  MediaCallOptions,
  MediaOperateOptions,
  Session,
  MediaSDK,
  SubscribeType,
  UserMediaTypes,
} from '@halo/chat-sdk';
import {
  MediaSDKDeviceType,
  MediaAction,
  MediaSDKMediaType,
  MsgPayloadType,
  MediaStatus,
  CallStatus,
  isMediaEnd,
  isSessionCategory,
  SessionCategoryPrefix,
  PermissionName,
  PermissionStatusState,
  MediaCallOperateCode,
} from '@halo/chat-sdk';
import { type StateCreatorEnhance } from '@halo/zustand-enhance';
import { promiseTimeout, safeJsonParse, type RequiredSome } from '@halo/utils';
import type { MediaStore } from './main';
import { debouncePromise, PromiseResolver } from './helper';

export interface MediaStateStore {
  client: Client;
  session: Session;
  media: MediaSDK;
  type: MediaType;
  room_id: string;
  status: MediaStatus | undefined;
  callStatus: CallStatus | undefined;
  token: string;
  name: string;
  user: User;
  inviteUsers: User[] | undefined;
  mediaUser: MediaMember | undefined;
  deviceId: string;
  devices: Record<
    MediaSDKDeviceType,
    {
      list: MediaDeviceInfo[];
      activatedId: string;
      status: MediaDeviceStatus;
    }
  >;
  screenSharing: {
    [MediaSDKMediaType.SCREENSHARING_VIDEO]: {
      status: MediaScreenSharingStatus;
    };
    [MediaSDKMediaType.SCREENSHARING_AUDIO]: {
      status: MediaScreenSharingStatus;
    };
  };
  caller: User;
  membersId: string[];
  membersMap: Record<string, MediaMember>;
  effects: Record<string, () => void>;
  devicesEffects: Record<string, () => void>;
  isSingleSession: boolean;
  callTimer: NodeJS.Timeout;
  ringingCancel: boolean;
  fullScreen: boolean;
  roomNetwork: Record<string, any>;
  reports: Record<string, any>[];
  hangupTrigger: PromiseResolver<unknown> | undefined;
  isOtherDeviceJoinCall: boolean;
  inviteTimeouts: Record<number, string[]>;
  invitingMembers: string[];
  subscribeMembersMap: Record<string, UserMediaTypes[]>;
  calleeAnswertimer: NodeJS.Timeout | undefined;
  devicessPermission: { permissionName: PermissionName; permissionStatus: PermissionStatus }[];
  isRequestingDevices: boolean;
  createHangupTrigger: () => Promise<unknown>;
  createMediaState: (options: CreateMediaStateOptions) => () => void;
  handleAction: (action: MediaAction, fromSelf: boolean) => void;
  call: (options: Pick<MediaCallOptions, 'type' | 'membersId'>) => Promise<void>;
  join: (options?: { joinCallType?: MediaType }) => Promise<void>;
  invite: (options: Pick<MediaCallOptions, 'membersId'>) => Promise<void>;
  hangup: () => Promise<void>;
  decline: () => Promise<void>;
  callTimeout: () => Promise<void>;
  callee: (options: { room_id: string }) => Promise<void>;
  operate: (options: Pick<MediaOperateOptions, 'action'> & { maxNum?: number }) => Promise<void>;
  getToken: (
    options?: Parameters<MediaApi['getToken']>[0] & { maxNum?: number }
  ) => ReturnType<MediaApi['getToken']>;
  syncInfo: () => Promise<void>;
  startCalling: () => void;
  stopCalling: () => void;
  addInviteTimeout: (memberIds: string[]) => void;
  removeMembersFromAllTimeouts: (memberIds: string[]) => void;
  setFullScreen: (fullScreen: boolean) => void;
  updateMembers: (members: RequiredSome<MediaMember, 'user_id'>[]) => void;
  updateMembersStreams: (members: RequiredSome<MediaMember, 'user_id'>[]) => void;
  getMember: (id: string) => MediaMember;
  joinSDKRoom: (options: {
    type: MediaType;
    joinCallType?: MediaType;
    room_id: string;
    token: string;
    user_id: string;
  }) => Promise<void>;
  getSDKDevicesStatus: () => Promise<void>;
  checkDevicesPermission: (permissionNames: PermissionName[]) => Promise<void>;
  updateDevices: () => Promise<Record<MediaSDKDeviceType, MediaDeviceInfo[]>>;
  selectDevice: (deviceType: MediaSDKDeviceType, deviceId: string) => Promise<void>;
  enableDevice: (deviceType: MediaSDKDeviceType, enable: boolean) => Promise<void>;
  switchEnableDevice: (deviceType: MediaSDKDeviceType) => Promise<void>;
  enableScreenSharing: (enable: boolean) => Promise<void>;
  switchEnableScreenSharing: () => Promise<void>;
  drawSpectrum: (
    id: string,
    canvas: HTMLCanvasElement,
    frameId: string,
    onDrawBefrore?: () => Promise<boolean>,
    onDrawEnd?: () => void,
    onData?: (data: Uint8Array<ArrayBuffer>) => void
  ) => Promise<void>;
  getVolume: (id: string) => number;
  getReportsByMid: (mids?: string[]) => Promise<any[]>;
  setLocalStream: (mediaType: MediaSDKMediaType[], enable: boolean) => Promise<void>;
  initWatches: () => void;
  destroy: () => Promise<void>;
  subscribeAndStop: (type: SubscribeType) => Promise<void>;
  subscribeDebouncePromise: (type: SubscribeType) => Promise<void>;
  subscribeCollection: (members: Record<string, any>[], type: SubscribeType) => Promise<any>;
}

export type CreateMediaStateOptions = RequiredSome<
  MediaStateStore,
  'client' | 'session' | 'user' | 'media' | 'deviceId'
>;

export enum MediaDeviceStatus {
  DISABLED,
  ENABLED,
  UNPUBLISHED,
  ERROR,
}

export enum MediaScreenSharingStatus {
  DISABLED,
  ENABLED,
}

const mediaToDeviceTypeMap = {
  [MediaSDKMediaType.MICROPHONECAMERA_AUDIO]: MediaSDKDeviceType.AUDIO_INPUT,
  [MediaSDKMediaType.MICROPHONECAMERA_VIDEO]: MediaSDKDeviceType.VIDEO_INPUT,
};

const deviceToMediaTypeMap = {
  [MediaSDKDeviceType.AUDIO_INPUT]: MediaSDKMediaType.MICROPHONECAMERA_AUDIO,
  [MediaSDKDeviceType.VIDEO_INPUT]: MediaSDKMediaType.MICROPHONECAMERA_VIDEO,
};

export const mediaStoreCreator: StateCreatorEnhance<MediaStore> = (set, get, instance) =>
  ({
    membersId: [],
    membersMap: {},
    devices: {
      [MediaSDKDeviceType.AUDIO_INPUT]: {
        list: [],
        activatedId: '',
        status: MediaDeviceStatus.DISABLED,
      },
      [MediaSDKDeviceType.AUDIO_OUTPUT]: {
        list: [],
        activatedId: '',
        status: MediaDeviceStatus.DISABLED,
      },
      [MediaSDKDeviceType.VIDEO_INPUT]: {
        list: [],
        activatedId: '',
        status: MediaDeviceStatus.DISABLED,
      },
    },
    screenSharing: {
      [MediaSDKMediaType.SCREENSHARING_VIDEO]: {
        status: MediaScreenSharingStatus.DISABLED,
      },
      [MediaSDKMediaType.SCREENSHARING_AUDIO]: {
        status: MediaScreenSharingStatus.DISABLED,
      },
    },
    effects: {},
    devicesEffects: {},
    ringingCancel: false,
    fullScreen: false,
    inviteTimeouts: {},
    invitingMembers: [],
    subscribeMembersMap: {},
    isRequestingDevices: false,
    createMediaState: (options) => {
      set({
        isSingleSession: isSessionCategory(options.session.chat_id, SessionCategoryPrefix.Single),
        status: MediaStatus.CREATED,
        ...options,
      });

      const { client } = options;

      const store = get();
      const {
        initWatches,
        syncInfo,
        handleAction,
        subscribeAndStop,
        checkDevicesPermission,
        isSingleSession,
        user,
      } = store;

      store.effects.mediaCallStatusChanged = client.addEventListener(
        'MediaCallStatusChanged',
        async ({ detail }) => {
          const { ope_type, chat_id, sender_id } = detail;
          const { status } = get();
          if (chat_id === options.session.chat_id) {
            const isCalleeAnswer =
              isSingleSession &&
              sender_id !== user.user_id &&
              ope_type === MediaAction.CALL_PUT_THROUGH;

            const isCalleeAnswerAndSelfCall = isCalleeAnswer && status === MediaStatus.CALL;

            if (isCalleeAnswerAndSelfCall) {
              set({ callStatus: CallStatus.CONNECTING });
            }

            const isOtherDeviceReject =
              ope_type === MediaAction.CALL_REJECTED && sender_id === user.user_id;

            await syncInfo();
            handleAction(ope_type, isCalleeAnswerAndSelfCall || isOtherDeviceReject || false);
          }
        }
      );

      const subscribeDebouncePromise = debouncePromise(subscribeAndStop);
      set({ subscribeDebouncePromise });
      initWatches();
      checkDevicesPermission([PermissionName.CAMERA, PermissionName.MICROPHONE]);
    },
    handleAction: (action, fromSelf) => {
      const { status, callStatus } = get();

      const isOtherDeviceAnswer =
        action === MediaAction.CALL_PUT_THROUGH &&
        !fromSelf &&
        status === MediaStatus.CALLEE &&
        callStatus === CallStatus.RINGING;

      if (isMediaEnd(action) || isOtherDeviceAnswer) {
        if (fromSelf || isOtherDeviceAnswer) {
          set({
            status: MediaStatus.CREATED,
            callStatus: isOtherDeviceAnswer ? CallStatus.OTHER_DEVICE_ANSWER : undefined,
          });
        }
      } else if (action === MediaAction.CALL_PUT_THROUGH) {
        if (fromSelf) {
          set({ status: MediaStatus.JOIN, callStatus: CallStatus.CONNECT });
        }
      }
    },
    call: async ({ type, membersId }) => {
      const {
        client,
        session,
        updateMembers,
        joinSDKRoom,
        getToken,
        updateDevices,
        join,
        user,
        isSingleSession,
        effects,
      } = get();

      set({
        callStatus: CallStatus.RINGING,
        membersId: isSingleSession ? membersId.concat(user.user_id) : [user.user_id],
      });

      effects.callResolveUsersInfo?.();
      const result = await client.Api.user.resolveUsersInfo(membersId, (members) => {
        updateMembers(members.concat(user));
      });
      effects.callResolveUsersInfo = result.removeListener;

      const {
        room_id,
        rtc_token: token,
        user_id,
      } = await getToken().catch((error) => {
        set({ status: undefined, callStatus: undefined });
        throw error;
      });
      set({ type, room_id, token });

      const callRes = await client.Api.media
        .call({
          session_id: session.chat_id,
          type,
          membersId,
          room_id,
        })
        .catch((error) => {
          set({ status: undefined, callStatus: undefined });
          throw error;
        });

      if (callRes?.code === MediaCallOperateCode.TARGET_CALLING_YOU) {
        // Get caller room_id
        const room_id = safeJsonParse(callRes.message, {} as any)?.room_id;
        set({ status: MediaStatus.CALL, room_id });
        return join();
      } else if (callRes?.code !== MediaCallOperateCode.SUCCESS) {
        if (callRes?.code === MediaCallOperateCode.SELF_IN_CALL) {
          if (isSingleSession) {
            set({ status: undefined, callStatus: undefined });
            throw callRes;
          }
        } else {
          set({ status: undefined, callStatus: undefined });
          throw callRes;
        }
      }

      set({ status: MediaStatus.CALL });

      await joinSDKRoom({
        type,
        room_id,
        token,
        user_id,
      }).catch((err) => {
        set({ status: undefined, callStatus: undefined });
        throw err;
      });

      updateDevices();

      set((prev) => {
        if (prev.ringingCancel) {
          return {
            status: MediaStatus.CREATED,
            callStatus: undefined,
          };
        } else {
          return isSingleSession
            ? {}
            : {
                status: MediaStatus.JOIN,
                callStatus: CallStatus.CONNECT,
              };
        }
      });
    },
    async join({ joinCallType } = {}) {
      const { type, room_id, updateDevices, joinSDKRoom, operate, getToken } = get();
      set({ callStatus: CallStatus.CONNECTING });
      const { rtc_token: token, user_id } = await getToken({ room_id }).catch((error) => {
        set({ status: undefined, callStatus: undefined });
        throw error;
      });
      set({ token });

      await joinSDKRoom({
        type,
        joinCallType,
        room_id,
        token,
        user_id,
      }).catch((err) => {
        set({ status: undefined, callStatus: undefined });
        throw err;
      });

      updateDevices();

      await operate({ action: MediaAction.CALL_PUT_THROUGH });
    },
    invite: async ({ membersId }) => {
      const { client, session, type, room_id, addInviteTimeout } = get();
      const callRes = await client.Api.media
        .call({
          session_id: session.chat_id,
          type,
          membersId,
          room_id,
        })
        .catch((error) => {
          throw error;
        });

      if (callRes?.code !== MediaCallOperateCode.SUCCESS) {
        throw callRes;
      }
      addInviteTimeout(membersId);
    },
    createHangupTrigger() {
      const promiseResolver = new PromiseResolver();
      set({ hangupTrigger: promiseResolver });
      return promiseResolver.promise;
    },
    async hangup() {
      const { status, callStatus, operate, createHangupTrigger } = get();
      if (
        status !== MediaStatus.CALL &&
        status !== MediaStatus.JOIN &&
        callStatus == CallStatus.RINGING
      ) {
        set({ callStatus: undefined, ringingCancel: true });
        return;
      }
      const trigger = createHangupTrigger();
      await operate({
        action:
          status === MediaStatus.JOIN ? MediaAction.CALL_HANG_UP : MediaAction.CALL_CANCELLED,
      });
      return await trigger;
    },
    callee: async ({ room_id }) => {
      const { syncInfo } = get();
      set({ room_id, status: MediaStatus.CALLEE, callStatus: CallStatus.RINGING });
      await syncInfo();
    },
    decline: async () => {
      const { operate } = get();
      await operate({
        action: MediaAction.CALL_REJECTED,
      });
    },
    callTimeout: async () => {
      const { operate } = get();
      await operate({
        action: MediaAction.CALL_TIMEOUT,
      });
    },
    operate: async ({ action, maxNum = 1 }) => {
      const { client, room_id, handleAction, operate } = get();
      const operateRes = await client.Api.media
        .operate({
          room_id,
          action,
        })
        .catch((err) => err);
      if (operateRes?.code !== 0 && operateRes?.code !== 1) {
        if (maxNum > 0) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          await operate({ action, maxNum: maxNum - 1 });
        } else if (MediaAction.CALL_PUT_THROUGH === action) {
          throw operateRes;
        }
      }
      handleAction(action, true);
    },
    getToken: async ({ maxNum = 1, ...options } = {}) => {
      const { client, getToken } = get();
      const result = await client.Api.media.getToken(options).catch((err) => err);
      if (!result?.room_id) {
        if (maxNum > 0) {
          return await getToken({ maxNum: maxNum - 1, ...options });
        } else {
          throw result;
        }
      }
      return result;
    },
    syncInfo: async () => {
      const {
        client,
        room_id,
        caller,
        inviteUsers,
        effects,
        user,
        deviceId: userDeviceId,
        updateMembers,
        removeMembersFromAllTimeouts,
      } = get();
      const info = await client.Api.media.getInfo({ room_id });

      if (!info?.create_user || !info?.members?.length) {
        set({ status: undefined, callStatus: undefined });
        return;
      }

      set({
        type: info.type === 1 ? MsgPayloadType.MEDIA_AUDIO_CALL : MsgPayloadType.MEDIA_VIDEO_CALL,
      });

      if (!caller) {
        effects.syncResolveUsersInfo?.();
        const result = await client.Api.user.resolveUsersInfo(info.create_user, (caller) => {
          set({ caller });
        });
        effects.syncResolveUsersInfo = result.removeListener;
      }

      const membersId = info.members.map((member) => member.user_id);
      const isOtherDeviceJoinCall = !!membersId.find(
        (user_id) =>
          user_id !== `${user.user_id}.${userDeviceId}` &&
          user_id?.includes(user?.user_id) &&
          user_id?.split('.').length === 2
      );
      set({ membersId, isOtherDeviceJoinCall });

      removeMembersFromAllTimeouts(membersId);

      if (!inviteUsers && info?.invite_users?.length) {
        const result_ = await client.Api.user.resolveUsersInfo(info?.invite_users, (users) => {
          set({ inviteUsers: users || [] });
        });
        result_.removeListener();
      }

      effects.syncResolveUsersInfo?.();
      const result = await client.Api.user.resolveUsersInfo(
        membersId.map((id) => id.split('.')[0]),
        (users) => {
          updateMembers(
            membersId.map((id, i) => {
              const user = users[i];
              return user && { ...user, user_id: id };
            })
          );
        }
      );
      effects.syncResolveUsersInfo = result.removeListener;
    },
    startCalling: () => {
      const callTimer = setTimeout(() => {
        const { callTimeout } = get();
        callTimeout();
      }, 60 * 1000);
      set({ callTimer });
    },
    stopCalling: () => {
      const { callTimer } = get();
      if (callTimer) {
        clearTimeout(callTimer);
      }
      set({ callTimer: undefined });
    },
    addInviteTimeout: (membersId) => {
      const key = Date.now() + 60000;
      set((state) => {
        const inviteTimeouts = { ...state.inviteTimeouts, [key]: membersId };
        return { inviteTimeouts, invitingMembers: Object.values(inviteTimeouts).flat() };
      });
    },
    removeMembersFromAllTimeouts: (membersId) => {
      set((state) => {
        const inviteTimeouts = Object.entries(state.inviteTimeouts).reduce(
          (acc, [key, inviteMembers]) => {
            if (Number(key) < Date.now()) {
              return acc;
            }
            const filteredMembers = inviteMembers.filter(
              (userId) => !membersId.find((user_id) => user_id.includes(userId))
            );
            if (filteredMembers.length > 0) {
              acc[key] = filteredMembers;
            }
            return acc;
          },
          {}
        );
        return {
          inviteTimeouts,
          invitingMembers: Object.values(inviteTimeouts).flat(),
        };
      });
    },
    setFullScreen: (fullScreen) => set({ fullScreen }),
    updateMembers: (members) => {
      set((store) => {
        members.forEach((member) => {
          if (member) {
            const user_id = member.user_id.split('.')[0];
            const oldInitMember = store.membersMap[user_id];
            const oldMember = store.membersMap[member.user_id] || oldInitMember;
            if (oldInitMember) {
              const index = store.membersId.indexOf(user_id);
              if (index !== -1) {
                store.membersId.splice(index, 1, member.user_id);
              }
              delete store.membersMap[user_id];
            }

            if (member.tracks) {
              const streams = (oldMember?.streams || {}) as MediaMember['streams'];
              const haveMediaTypes = [];
              member.tracks.forEach((track) => {
                haveMediaTypes.push(track.mediaType);
                const stream = streams[track.mediaType] || {
                  enabled: true,
                  stream: undefined,
                };
                stream.enabled = track.enabled;
                streams[track.mediaType] = stream;
                if (track.mediaType === MediaSDKMediaType.MICROPHONECAMERA_VIDEO) {
                  member.videoMid = track.mid;
                }
                if (track.mediaType === MediaSDKMediaType.SCREENSHARING_VIDEO) {
                  member.screenMid = track.mid;
                }
              });

              Object.keys(streams).forEach((key) => {
                if (!haveMediaTypes.includes(key)) {
                  streams[key].enabled = false;
                }
              });

              member.streams = streams;
            }

            store.membersMap[member.user_id] = { ...oldMember, ...member };
          }
        });
      });

      // singleSession callee CALL_PUT_THROUGH notice lose
      const { isSingleSession, calleeAnswertimer, user } = get();
      const calleeRoomMember = members.find(
        (member) => !member.user_id.includes(user.user_id) && member.id
      );
      if (isSingleSession && calleeRoomMember) {
        if (calleeAnswertimer) {
          clearTimeout(calleeAnswertimer);
        }
        const timer = setTimeout(() => {
          const { calleeAnswertimer, status, callStatus, handleAction } = get();
          if (status === MediaStatus.CALL && callStatus === CallStatus.RINGING) {
            handleAction(MediaAction.CALL_PUT_THROUGH, true);
          }
          clearTimeout(calleeAnswertimer);
        }, 5000);
        set({ calleeAnswertimer: timer });
      }
    },
    updateMembersStreams: (members) => {
      const { membersMap, media, updateMembers } = get();
      const newMembers = members.map((member) => {
        const oldMember = membersMap[member.user_id];
        let streams = (oldMember?.streams || {}) as MediaMember['streams'];

        if (member.updateStreams) {
          for (const k in member.updateStreams) {
            const action = (member.updateStreams[k] = Boolean(member.updateStreams[k])); // TODO amo: If from 'onRoomUsersStreams' this value is a string.
            const stream = {
              enabled: true,
              stream: undefined,
              ...streams[k as MediaSDKMediaType],
            };
            media.logger.log('getUserStream action', member.user_id, k, action);
            if (action) {
              stream.enabled = true;
              stream.stream = media.quickvo.getUserStream(member.user_id, k as MediaSDKMediaType);
              media.logger.log('getUserStream', member.user_id, k, stream.stream);
            } else {
              stream.enabled = false;
              stream.stream = undefined;
            }

            streams = {
              ...streams,
              [k as MediaSDKMediaType]: stream,
            };
          }

          return {
            user_id: member.user_id,
            streams,
          };
        }
      });

      updateMembers(newMembers);
    },
    getMember: (id) => {
      const { membersMap } = get();
      return membersMap[id];
    },
    joinSDKRoom: async ({ type, joinCallType, room_id, token, user_id }) => {
      const { media, updateMembers } = get();

      const members = await media.joinRoom({
        media_types:
          type === MsgPayloadType.MEDIA_AUDIO_CALL ||
          joinCallType === MsgPayloadType.MEDIA_AUDIO_CALL
            ? [MediaSDKMediaType.MICROPHONECAMERA_AUDIO]
            : [
                MediaSDKMediaType.MICROPHONECAMERA_AUDIO,
                MediaSDKMediaType.MICROPHONECAMERA_VIDEO,
              ],
        userId: user_id,
        roomId: room_id,
        sdkToken: token,
        callType: 1,
        newPublishAutoSubscribe: false,
      });

      updateMembers(
        members.map((member) => ({
          user_id: member.id,
          ...member,
        }))
      );
    },
    checkDevicesPermission: async (permissionNames) => {
      const { devicesEffects, updateDevices, switchEnableDevice } = get();
      const devicessPermission = await Promise.all(
        permissionNames.map(async (permissionName) => {
          const permissionStatus = await navigator.permissions.query({ name: permissionName });
          return { permissionName, permissionStatus };
        })
      );

      if (devicessPermission) {
        set({ devicessPermission });

        devicessPermission.forEach((permission) => {
          const change = async () => {
            set((store) => {
              store.devicessPermission = store.devicessPermission.map((prePermission) => {
                if (prePermission.permissionStatus.name === permission.permissionStatus.name) {
                  return permission;
                }
                return prePermission;
              });
            });
            await updateDevices();
            const { devices } = get();
            const deviceType =
              permission.permissionName === PermissionName.CAMERA
                ? MediaSDKDeviceType.VIDEO_INPUT
                : MediaSDKDeviceType.AUDIO_INPUT;

            const device = devices[deviceType];
            if (
              (permission.permissionStatus.state === PermissionStatusState.GRANTED &&
                [MediaDeviceStatus.UNPUBLISHED, MediaDeviceStatus.DISABLED].includes(
                  device.status
                )) ||
              (permission.permissionStatus.state === PermissionStatusState.DENIED &&
                [MediaDeviceStatus.ENABLED].includes(device.status))
            ) {
              switchEnableDevice(deviceType);
            }
          };
          permission.permissionStatus.addEventListener('change', change);
          devicesEffects[`deviceChange_${permission.permissionName}`] = () => {
            permission.permissionStatus.removeEventListener('change', change);
          };
        });
      }
    },
    updateDevices: async () => {
      const { media, isRequestingDevices } = get();
      if (isRequestingDevices) {
        return;
      }
      set({ isRequestingDevices: true });

      const [devices, devicesActivatedId] = await Promise.all([
        media.getDevices(),
        media.getDevicesActivatedId(),
      ]).catch(() => [{}, null]);

      set({ isRequestingDevices: false });
      set((store) => {
        Object.entries(devices).forEach(([key, value]) => {
          store.devices[key].list = value;
          let activatedId = devicesActivatedId[key];
          if (value.length && !value.some((item) => item.deviceId === activatedId)) {
            activatedId =
              value.find((item) => item.deviceId === 'default')?.deviceId || value[0].deviceId;
          }
          media.quickvo.setMediaDeviceKind(key as MediaSDKDeviceType, activatedId);
          store.devices[key].activatedId = activatedId;
        });
      });
      return devices;
    },
    async selectDevice(deviceType, deviceId) {
      const { media } = get();
      const logCb = media.logger.asyncLog('selectDevice', { deviceType, deviceId });
      const res = await media.quickvo.setMediaDeviceKind(deviceType, deviceId);
      set((store) => {
        store.devices[deviceType].activatedId = deviceId;
      });
      logCb(res);
    },
    enableDevice: async (deviceType, enable: boolean) => {
      const { media, devices } = get();
      const device = devices[deviceType];
      const mediaType: MediaSDKMediaType = deviceToMediaTypeMap[deviceType];

      switch (deviceType) {
        case MediaSDKDeviceType.AUDIO_INPUT:
        case MediaSDKDeviceType.VIDEO_INPUT:
          if (enable) {
            switch (device.status) {
              case MediaDeviceStatus.DISABLED: {
                const logCb = media.logger.asyncLog('enableDevice:pauseTracks', mediaType, true);
                const res = await media.quickvo.pauseTracks([mediaType], false);
                media.quickvo.setMediaDeviceKind(deviceType, device.activatedId);
                logCb(res);
                break;
              }
              case MediaDeviceStatus.UNPUBLISHED: {
                const logCb = media.logger.asyncLog('enableDevice:publish', mediaType);
                const res = await media.quickvo.publish([mediaType]);
                logCb(res);
                break;
              }
            }
          } else {
            switch (device.status) {
              case MediaDeviceStatus.ENABLED: {
                const logCb = media.logger.asyncLog('enableDevice:pauseTracks', mediaType, false);
                const res = await media.quickvo.pauseTracks([mediaType], true);
                logCb(res);
                break;
              }
            }
          }
          break;
        case MediaSDKDeviceType.AUDIO_OUTPUT:
          // TODO amo: Implement audio output
          break;
      }
    },
    switchEnableDevice: async (deviceType) => {
      const { devices, enableDevice } = get();
      const device = devices[deviceType];
      switch (device.status) {
        case MediaDeviceStatus.ENABLED:
          await enableDevice(deviceType, false);
          break;
        case MediaDeviceStatus.UNPUBLISHED:
        case MediaDeviceStatus.DISABLED:
          await enableDevice(deviceType, true);
          break;
      }
    },
    enableScreenSharing: async (enable: boolean) => {
      const { media } = get();
      const logCb = media.logger.asyncLog('enableScreenSharing', enable);
      const res = await media.quickvo[enable ? 'publish' : 'stopPublish']([
        MediaSDKMediaType.SCREENSHARING_VIDEO,
        MediaSDKMediaType.SCREENSHARING_AUDIO,
      ]).catch();
      logCb(res);
    },
    switchEnableScreenSharing: async () => {
      const { mediaUser, enableScreenSharing } = get();
      if (mediaUser?.callActionMap) {
        await enableScreenSharing(
          !(
            mediaUser.callActionMap[MediaSDKMediaType.SCREENSHARING_VIDEO] ||
            mediaUser.callActionMap[MediaSDKMediaType.SCREENSHARING_AUDIO]
          )
        );
      }
    },
    drawSpectrum: async (
      id: string,
      canvas: HTMLCanvasElement | undefined,
      frameId: string,
      onDrawBefrore?: () => Promise<boolean>,
      onDrawEnd?: () => void,
      onData?: (data: Uint8Array<ArrayBuffer>) => void
    ) => {
      const { media } = get();
      await media.drawSpectrum(id, canvas, frameId, onDrawBefrore, onDrawEnd, onData);
    },
    getVolume: (id: string) => {
      const { media } = get();
      return media.quickvo.getVolume(id, MediaSDKMediaType.MICROPHONECAMERA_AUDIO);
    },
    getReportsByMid: async (mids: string[]) => {
      const { media } = get();
      return media.quickvo.getReportsByMid(mids);
    },
    initWatches: () => {
      // Get media user
      instance.subscribe((store, prev) => {
        if (
          store.mediaUser === prev.mediaUser &&
          store.membersMap === prev.membersMap &&
          store.user === prev.user &&
          store.deviceId === prev.deviceId
        ) {
          return;
        }

        store.media.logger.log('state:mediaStore', store);
        const { user, deviceId, membersMap, mediaUser: currentMediaUser } = store;
        const mediaUser = membersMap[`${user?.user_id}.${deviceId}`];
        if (mediaUser !== currentMediaUser) {
          set({ mediaUser });
        }
      });

      // Get devices status
      instance.subscribe((store, prev) => {
        if (store.mediaUser === prev.mediaUser && store.devices === prev.devices) {
          return;
        }

        const { mediaUser, devices } = store;
        set((store) => {
          Object.entries(mediaToDeviceTypeMap).forEach(([key, value]) => {
            const track = mediaUser?.tracks?.find((track) => track.mediaType === key);
            const status = track
              ? track.enabled
                ? MediaDeviceStatus.ENABLED
                : MediaDeviceStatus.DISABLED
              : devices[value].list.length
                ? MediaDeviceStatus.UNPUBLISHED
                : MediaDeviceStatus.ERROR;

            store.devices[value].status = status;
          });

          store.devices[MediaSDKDeviceType.AUDIO_OUTPUT].status = devices[
            MediaSDKDeviceType.AUDIO_OUTPUT
          ].list.length
            ? MediaDeviceStatus.ENABLED
            : MediaDeviceStatus.ERROR;
        });
      });

      // Get screen sharing status
      instance.subscribe((store, prev) => {
        if (store.mediaUser === prev.mediaUser) {
          return;
        }

        const { mediaUser } = store;

        set((store) => {
          [MediaSDKMediaType.SCREENSHARING_VIDEO, MediaSDKMediaType.SCREENSHARING_AUDIO].forEach(
            (mediaType) => {
              const track = mediaUser?.tracks?.find((track) => track.mediaType === mediaType);
              store.screenSharing[mediaType].status = track?.enabled
                ? MediaScreenSharingStatus.ENABLED
                : MediaScreenSharingStatus.DISABLED;
            }
          );
        });
      });
    },
    async subscribeAndStop(type) {
      const { subscribeMembersMap, media } = get();
      if (Object.keys(subscribeMembersMap[type]).length === 0) return;
      return promiseTimeout(
        () =>
          (type === 'subscribe' ? media.subscribe : media.stopSubscribe).call(
            media,
            Object.values(subscribeMembersMap[type])
          ),
        5000
      )?.finally(() => {
        set((state) => ({
          subscribeMembersMap: {
            ...state.subscribeMembersMap,
            [type]: {},
          },
        }));
      });
    },

    async subscribeCollection(members, type) {
      const { subscribeDebouncePromise } = get();
      set((state) => {
        const updatedMembers = { ...state.subscribeMembersMap[type] };
        members.forEach((member) => {
          if (!updatedMembers[member.id]) {
            updatedMembers[member.id] = {
              userId: member.user_id,
              mediaTypes: member?.screenSharing
                ? [MediaSDKMediaType.SCREENSHARING_VIDEO]
                : [MediaSDKMediaType.MICROPHONECAMERA_VIDEO],
            };
          }
        });

        return {
          subscribeMembersMap: {
            ...state.subscribeMembersMap,
            [type]: updatedMembers,
          },
        };
      });
      return subscribeDebouncePromise(type);
    },
    destroy: async () => {
      const { effects, devicesEffects, media } = get();
      const logCb = media.logger.asyncLog('destroy');
      Object.values(effects).forEach((handler) => handler());
      Object.values(devicesEffects).forEach((handler) => handler());
      logCb('destroy');
    },
  }) as MediaStore;
